import React, { useContext } from "react";
import {
  Badge,
  Text,
  LegacyCard as Card,
  Link,
  Thumbnail,
  InlineStack,
  Box,
  Divider,
  BlockStack,
} from "@shopify/polaris";
import { PrivateContext } from "lib/context";
import constant from "lib/constant/constant";
import { baseHelper, imageHelper, statusHelper } from "lib/helpers";
import LineItemProperties from "app/orders/modules/generic/service/lineItemProperties";
import { StyledCard } from "app/orders/modules/operator/features/view/genericStyled";
import { useNavigate } from "react-router-dom";

const RejectedLineItems = (props) => {
  const { data = {}, lineItemArray = [], refundLineData = [], setRejectAmount } = props;
  const { cms, userData } = useContext(PrivateContext);
  const navigate = useNavigate();
  const currentUserData = baseHelper.getResponseData(userData, constant.gql.GET_CURRENT_USER);
  const amount = [];
  const vendorsSum = [];
  let countItems = 0;
  refundLineData &&
    refundLineData.length &&
    refundLineData.forEach((dataLine) => {
      const { totalPrice = 0, line_item: itemLine = {}, totalQuantity = 0 } = dataLine || {};
      const { id = "", price = 0, vendor } = itemLine || {};
      const getItem = lineItemArray.find((item) => item.shopifyLineItemId === id);
      const { markUpPrice = 0, sellerDiscount = 0 } = getItem || {};
      vendorsSum.push(vendor);
      amount.push(
        parseFloat(totalPrice) - parseFloat(markUpPrice * totalQuantity) + parseFloat(sellerDiscount * totalQuantity)
      );
      countItems += 1;
      return [amount];
    });

  const vendorSet = new Set(vendorsSum);
  const setSize = vendorSet.size;
  const addVendorTxt =
    setSize === constant.value.ONE ? `${setSize} ${cms("label.vendor")}` : `${setSize} ${cms("label.vendors")}`;

  const isVendor = baseHelper.isVendor(currentUserData);

  const stringToNumber = amount.map((item) => parseFloat(item));
  const totalAmount = stringToNumber.reduce((cur, acc) => cur + acc, constant.value.ZERO);

  if (totalAmount) {
    setRejectAmount(totalAmount);
  }

  const lineItems =
    refundLineData &&
    refundLineData.length &&
    refundLineData.map((lineData, idx) => {
      const { line_item: itemLine = {}, line_item_id: shopifyLineItemId = "", totalPrice, totalQuantity } =
        lineData || {};
      const { sku = "", title = "" } = itemLine || {};
      const getItem = lineItemArray.find((item) => item.shopifyLineItemId === shopifyLineItemId);
      const {
        fulfillmentType = "",
        image = "",
        isAutoExpiry,
        isProductDeleted,
        isAutoReject,
        isRefund,
        isVendorDeleted,
        markUpPrice = 0,
        moneyFormat: lineMoneyFormat,
        productId,
        properties,
        sellerDiscount = 0,
        vendor,
        vendorId,
      } = getItem || {};
      const lineTotalPrice =
        parseFloat(totalPrice) - parseFloat(markUpPrice * totalQuantity) + parseFloat(sellerDiscount * totalQuantity);
      const lineItemKey = `lineItem${idx}`;
      const lineImage = imageHelper.resize({ url: image, type: constant.imageTypes.THUMBNAIL }) || constant.NOIMAGESNAP;

      const moneyFormat = lineMoneyFormat || constant.symbol.DOLLAR;
      const autoStatus = isRefund && isAutoReject ? constant.AUTO_REJECT : isAutoExpiry && constant.NO_SHOW;
      const status = statusHelper.getBadgeStatus(autoStatus || constant.REJECTED);
      const lineItemName = <div className="productName">{title}</div>;
      const isServiceLineItem = baseHelper.isServiceLineItem(fulfillmentType, properties);
      const productURL = isServiceLineItem ? `/products/service/edit/${productId}` : `/products/edit/${productId}`;

      return (
        <>
          <div key={lineItemKey} className="fulfilled-Secn">
            <Box padding={"400"}>
              <StyledCard className="styled-card-unfulfill">
                <InlineStack wrap={false} align="space-between">
                  <InlineStack gap={"400"}>
                    <Thumbnail size="large" source={lineImage} alt={title} />
                    <BlockStack gap={"100"} inlineAlign="start">
                      {(isProductDeleted && (lineItemName || "")) || (
                        <Link onClick={() => navigate(productURL)}>{title || ""}</Link>
                      )}

                      <Text variant="bodySm" as="p">
                        <Text as="span" color={constant.SUBDUED}>
                          {vendor && vendorId
                            ? isVendor || isVendorDeleted
                              ? [`${cms("label.vendor")}: `, vendor, <br />]
                              : [
                                  `${cms("label.vendor")}: `,
                                  <Link onClick={() => navigate(constant.PROVIDER_VIEW + vendorId)}>
                                    {vendor || ""}
                                  </Link>,
                                  <br />,
                                ]
                            : ""}
                        </Text>
                        <LineItemProperties
                          variation={constant.SUBDUED}
                          properties={properties}
                          isServiceLineItem={isServiceLineItem}
                        />
                        {isServiceLineItem
                          ? ""
                          : sku && (
                              <Text as="span" color={constant.SUBDUED}>
                                {cms("label.skuList")}
                                {sku}
                              </Text>
                            )}
                      </Text>

                      <Badge tone="critical" className="text-capitalize">
                        {status}
                      </Badge>
                    </BlockStack>
                  </InlineStack>
                  <InlineStack gap={"400"}>
                    <Text as="span">{` ${cms("label.qty")}: ${totalQuantity}`}</Text>
                    <Text as="h5">
                      {moneyFormat}
                      {(lineTotalPrice && lineTotalPrice.toFixed(2)) || constant.value.ZERO}
                    </Text>
                  </InlineStack>
                </InlineStack>
              </StyledCard>
            </Box>
          </div>
          <Divider />
        </>
      );
    });

  const moneyFormat = data && data.moneyFormat;
  const totalFulfill = (
    <Card.Section>
      <InlineStack align="space-between">
        <Text as="span" fontWeight="semibold">
          {cms("label.total")}
        </Text>
        <InlineStack gap={"400"}>
          <Text variant="bodySm" as="p">
            {" "}
            (
            {countItems > 1
              ? `${countItems}
                ${cms("label.items")} from ${addVendorTxt}`
              : `${countItems}
                ${cms("label.item")} from ${addVendorTxt}`}
            )
          </Text>
          <Text as="span" fontWeight="semibold">
            {`${moneyFormat || constant.symbol.DOLLAR}${totalAmount.toFixed(2)}`}
          </Text>
        </InlineStack>
      </InlineStack>
    </Card.Section>
  );

  return [lineItems, totalFulfill];
};

export default RejectedLineItems;
